// src/hr/timeoff/SickReportDialog.js -- "Out sick today?" Sick days are reported, not requested:
// booked straight away, no approval, no reason. The body carries dates and length only.
import React, { useId, useState } from 'react';
import { FiAlertCircle, FiCheck } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Drawer, Field, Seg } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel } from '../format';
import { addDays, isDateString } from './dates';

const LENGTHS = [{ value: 8, label: 'Full day' }, { value: 4, label: 'Half day' }];
const PAST_DAYS = 14;
const AHEAD_DAYS = 7;

function SickForm({ me, today, onClose, onDone }) {
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const lenId = `hrp-sick-len-${plainId(useId())}`;
  const earliest = addDays(today, -PAST_DAYS);
  const latest = addDays(today, AHEAD_DAYS);
  const sick = ((me && me.balances) || []).find((b) => b.isSick);

  const changeStart = (value) => {
    setStartDate(value);
    if (isDateString(value) && (!isDateString(endDate) || endDate < value)) setEndDate(value);
  };

  const submit = async () => {
    if (busy) return;
    if (!isDateString(startDate) || !isDateString(endDate)) {
      setError('Pick valid dates');
      return;
    }
    if (endDate < startDate) {
      setError('The last day is before the first day');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await hr.reportSick({ startDate, endDate, hoursPerDay });
      onDone('Sick day recorded. Feel better.');
    } catch (err) {
      setError(errorText(err, "Couldn't record your sick day. Try again."));
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Report a sick day"
      subtitle="No reason needed."
      width={400}
      onSubmit={submit}
      className="hrp-sick"
      footer={(
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" icon={FiCheck} loading={busy}>Report sick day</Btn>
        </>
      )}
    >
      <div className="hrp-field">
        <div className="hrp-two">
          <Field label="First day" type="date" value={startDate} min={earliest} max={latest} onChange={(e) => changeStart(e.target.value)} required />
          <Field label="Last day" type="date" value={endDate} min={startDate || earliest} max={latest} onChange={(e) => setEndDate(e.target.value)} required />
        </div>
        <div className="hrp-field__hint">From the last 14 days up to 7 days ahead</div>
      </div>

      <div className="hrp-field">
        <div className="hrp-field__label" id={lenId}>Length per day</div>
        <Seg labelledBy={lenId} options={LENGTHS} value={hoursPerDay} onChange={setHoursPerDay} />
      </div>

      <div className="hrp-sick__info">
        <p className="hrp-sick__lead">It's booked right away as Sick &amp; Safe — no approval needed. Your approver and HR get a heads-up.</p>
        <p>Sick &amp; Safe covers your own or a family member's illness or care, safe time, school meetings and public-health closures.</p>
        {sick && sick.setUp && <p className="hrp-sick__left">{`Sick & Safe left: ${daysLabel(sick.availableHours)}`}</p>}
      </div>

      {error && (
        <div className="hrp-form-err" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </Drawer>
  );
}

export function SickReportDialog({ open, ...props }) {
  if (!open) return null;
  return <SickForm {...props} />;
}

export default SickReportDialog;
